import { useFlowSessionById } from "@/api/flow_sessions";
import { Badge } from "@/components/ui/badge";
import { useChatPresence } from "@/hooks/useChatPresence";
import type {
  CanvasChatSessionNodeData,
  CanvasNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { useStoreApi } from "@xyflow/react";
import { MessagesSquare } from "lucide-react";
import { memo, useCallback, useRef } from "react";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { CanvasPinAffordance } from "../components/CanvasPinAffordance";
import { ChatPanel, PANEL_HEIGHT, PANEL_WIDTH } from "../components/ChatPanel";
import { useCanvasApi } from "../context/CanvasContext";

type ChatSessionNodeRFProps = CanvasNodeProps<
  CanvasChatSessionNodeData,
  "chatSession"
>;

type ChatSessionNodeProps = ChatSessionNodeRFProps & {
  isExpanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  canvasId?: string;
};

export const ChatSessionNode = memo(function ChatSessionNode({
  id,
  data,
  positionAbsoluteX,
  positionAbsoluteY,
  selected,
  isExpanded = false,
  onToggleExpanded,
  canvasId,
}: ChatSessionNodeProps) {
  // The drag conversion reads the exact zoom off the store; the bubble and
  // panel take theirs from CanvasPinAffordance.
  const store = useStoreApi();
  const canvas = useCanvasApi();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpanded?.(!isExpanded);
    },
    [isExpanded, onToggleExpanded],
  );

  const handleDelete = useCallback(() => {
    if (id) canvas.removeNode(id);
    onToggleExpanded?.(false);
  }, [id, canvas, onToggleExpanded]);

  // Snapshot of the node's flow position at the moment header drag starts.
  // Captured synchronously at mousedown so it's never stale.
  const dragStartPosRef = useRef({ x: 0, y: 0 });

  const handlePanelDragStart = useCallback(() => {
    dragStartPosRef.current = { x: positionAbsoluteX, y: positionAbsoluteY };
  }, [positionAbsoluteX, positionAbsoluteY]);

  // Receives CUMULATIVE (dx, dy) from the drag-start point in screen space.
  // Applies them relative to the captured start position and snaps to the
  // canvas lattice — this panel drags itself (not via React Flow), so it
  // quantizes here like the helper-lines interceptor does for node drags.
  const handlePanelDrag = useCallback(
    (cumulativeDx: number, cumulativeDy: number) => {
      if (!id) return;
      // Exact zoom, read on demand: the conversion must not depend on the
      // stepped render value and must not put zoom in the deps.
      const exactZoom = store.getState().transform[2];
      const rawX = dragStartPosRef.current.x + cumulativeDx / exactZoom;
      const rawY = dragStartPosRef.current.y + cumulativeDy / exactZoom;
      canvas.updateNodePosition(id, {
        x: snapToGrid(rawX, LATTICE),
        y: snapToGrid(rawY, LATTICE),
      });
    },
    [id, canvas, store],
  );

  const { flowSessionId } = data;

  // Report presence while this quick-chat node is expanded (its thread visible),
  // so the backend suppresses $toolConfirmation notifications for it. The backend
  // refcounts viewed sessions, so multiple expanded nodes each track their own.
  useChatPresence(
    isExpanded && flowSessionId && canvasId ? flowSessionId : undefined,
  );

  const { data: flowSession } = useFlowSessionById(flowSessionId, {
    enabled: !!flowSessionId,
  });
  const sessionLabel =
    flowSession?.name?.trim() || data.firstMessage?.trim() || "Quick Chat";

  // Unread turns ride the badge; a streaming turn pulses it. The session's
  // follow-up messages are the only per-turn signal the API carries.
  const unreadTurns =
    flowSession?.read_status === "unread"
      ? (flowSession.follow_up_data?.messages?.length ?? 1)
      : 0;
  const streaming = flowSession?.status === "processing";

  return (
    <CanvasPinAffordance
      x={positionAbsoluteX}
      y={positionAbsoluteY}
      open={isExpanded && !!flowSessionId && !!canvasId}
      panelKey={flowSessionId}
      panelWidth={PANEL_WIDTH}
      panelHeight={PANEL_HEIGHT}
      onBubbleClick={handleClick}
      bubbleTitle={sessionLabel}
      bubbleClassName={cn(
        "cursor-pointer select-none",
        "transition-all duration-150",
        "hover:brightness-110 active:scale-95",
      )}
      bubble={
        <div
          className={cn(
            // Design-system chat bubble: rust circle, paper glyph, elev-4.
            "relative flex items-center justify-center",
            "w-10 h-10 rounded-full shadow-elev-4",
            "transition-all duration-200",
            isExpanded || selected
              ? "bg-rust ring-2 ring-canvas-selection/60"
              : "bg-rust hover:bg-[color-mix(in_oklab,var(--rust)_85%,var(--ink))]",
          )}
        >
          <MessagesSquare className="h-5 w-5 text-paper" strokeWidth={2} />
          {(unreadTurns > 0 || streaming) && (
            <Badge
              title={streaming ? "Streaming…" : `${unreadTurns} unread`}
              className={cn(
                "absolute -top-1 -right-1 justify-center border-2 border-neutral-0 dark:border-neutral-900",
                unreadTurns > 0
                  ? "min-w-4 px-1 py-0 text-[10px] leading-4"
                  : "size-2.5 animate-pulse bg-lc-warning p-0",
              )}
            >
              {unreadTurns > 0 ? unreadTurns : null}
            </Badge>
          )}
        </div>
      }
      panel={
        flowSessionId && canvasId ? (
          <ChatPanel
            flowSessionId={flowSessionId}
            canvasId={canvasId}
            onDragHeaderStart={handlePanelDragStart}
            onDragHeader={handlePanelDrag}
            onClose={() => onToggleExpanded?.(false)}
            onDelete={handleDelete}
          />
        ) : undefined
      }
    />
  );
});
